import { AddIcon, ChevronDownIcon, ChevronRightIcon, DeleteIcon } from "@chakra-ui/icons";
import { Badge, Box, Button, Flex, Icon, IconButton, Text, VStack } from "@chakra-ui/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { FaBalanceScale } from "react-icons/fa";
import { FiBookOpen } from "react-icons/fi";
import type { AwardCriterionDto, AwardSubCriterionDto } from "../../../autogen/bff-api";
import { rowOpenProps } from "../../../common/clickableRow";
import { useRequirementDescription } from "../../../common/library/describeRequirement";
import {
  libraryCriterionStatus,
  librarySubCriterionStatus,
} from "../../../common/library/awardLibraryStatus";
import { useProcurementTerms } from "../../../common/terminology/useProcurementTerms";
import { AwardStatusChip } from "../../sourcing-events/buyer/wizard/components/AwardStatusChip";
import { WeightInput } from "../../sourcing-events/buyer/wizard/components/WeightInput";
import { criterionTypeLabelKey } from "../../sourcing-events/buyer/wizard/utils/publicProcurementOptions";

// t('New sub-criterion') t('Choose from the library')
// t('Open') t('Delete criterion') t('{{count}} sub-criterion')
// t('Add sub-criterion') t('Add sub-criterion from the library')
// t('Delete sub-criterion') t('Expand {{criterion}}') t('Collapse {{criterion}}')
// t('Weight') t('Weight of {{requirement}}') t('Complete') t('Missing weight')
// t('Missing description') t('Sub-criterion weights {{total}} %') t('Not weighted')

interface Props {
  items: AwardCriterionDto[];
  onAdd: () => void;
  onEdit: (index: number) => void;
  onDelete: (index: number) => void;
  /**
   * Sub-criteria travel with the criterion, so they are managed where the criterion lives. Omitted by
   * callers that only display criteria, which leaves the rows unexpandable.
   */
  onAddSubCriterion?: (index: number) => void;
  onAddSubCriterionFromLibrary?: (index: number) => void;
  onEditSubCriterion?: (index: number, subIndex: number) => void;
  onDeleteSubCriterion?: (index: number, subIndex: number) => void;
  /**
   * Edits a requirement's share of its criterion straight from the row. Omitted by callers that hold
   * their criteria somewhere the weight cannot be written back to, which leaves the cell read-only.
   */
  onSubCriterionWeightChange?: (index: number, subIndex: number, weight: number | undefined) => void;
  readOnly?: boolean;
  /** Marks the last n rows as just added, so a batch from the library is easy to spot and undo. */
  markedCount?: number;
}

export const AwardCriterionList = ({
  items,
  onAdd,
  onEdit,
  onDelete,
  onAddSubCriterion,
  onAddSubCriterionFromLibrary,
  onEditSubCriterion,
  onDeleteSubCriterion,
  onSubCriterionWeightChange,
  readOnly = false,
  markedCount = 0,
}: Props) => {
  const { t } = useTranslation();
  const terms = useProcurementTerms();
  const describe = useRequirementDescription();

  // What a criterion is still missing, said where it is listed rather than only once it has been applied
  // to a procurement and the publish gate turns it down.
  const criterionChip = (criterion: AwardCriterionDto) => {
    const status = libraryCriterionStatus(criterion);
    switch (status.kind) {
      case "complete":
        return <AwardStatusChip testId="library-criterion-status" tone="success" label={t("Complete")} />;
      case "missing-description":
        return (
          <AwardStatusChip testId="library-criterion-status" tone="warning" label={t("Missing description")} />
        );
      case "sub-weights-invalid":
        return (
          <AwardStatusChip
            testId="library-criterion-status"
            tone="warning"
            label={t("Sub-criterion weights {{total}} %", { total: status.total })}
          />
        );
      // Neither of these is a problem: requirements are optional, and a set left unweighted is a
      // definition that hands the weighting to each procurement. Amber belongs to what a procurement
      // would actually reject.
      case "no-sub-criteria":
        return <AwardStatusChip testId="library-criterion-status" tone="neutral" label={t("No sub-criteria")} />;
      case "unweighted":
        return <AwardStatusChip testId="library-criterion-status" tone="neutral" label={t("Not weighted")} />;
    }
  };

  const subCriterionChip = (criterion: AwardCriterionDto, sub: AwardSubCriterionDto) => {
    const status = librarySubCriterionStatus(criterion, sub);
    // Nothing worth saying: the set carries no weights at all, and this one is described.
    if (status === null) return null;
    const label = {
      complete: "Complete",
      "missing-weight": "Missing weight",
      "missing-description": "Missing description",
    }[status];
    return (
      <AwardStatusChip
        testId="library-sub-criterion-status"
        tone={status === "complete" ? "success" : "warning"}
        label={t(label)}
      />
    );
  };
  const firstMarked = items.length - markedCount;
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const toggle = (index: number) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (!next.delete(index)) next.add(index);
      return next;
    });

  if (items.length === 0) {
    return (
      <Box
        borderWidth="1px"
        borderColor="smBorder"
        borderRadius="lg"
        bg="smBackgroundSecondary"
        py="12"
        px="6"
        textAlign="center"
      >
        <Icon as={FaBalanceScale} boxSize="10" color="smMuted" mb="4" />
        <Text fontWeight="bold" mb="1">
          {terms.noCriteriaAddedYet}
        </Text>
        {!readOnly && (
          <>
            <Text color="smMuted" fontSize="sm" mb="6">
              {terms.addReusableAwardCriteria}
            </Text>
            <Button colorScheme="teal" leftIcon={<AddIcon />} onClick={onAdd}>
              {terms.newCriterion}
            </Button>
          </>
        )}
      </Box>
    );
  }

  // One frame with rows inside it, like the criteria table the wizard uses — not a stack of cards.
  return (
    <Box borderWidth="1px" borderColor="smBorder" borderRadius="lg" overflow="hidden" bg="smBackground">
      {items.map((item, index) => {
        const detail = describe.award(item, { titleShowsType: !item.name });
        const subCriteria = item.subCriteria ?? [];
        const name = item.name || t(criterionTypeLabelKey(item.type));
        // Managing sub-criteria is what the row opens for; without a way to manage them, an empty
        // criterion has nothing to show and stays shut.
        const canManageSubs = !readOnly && onAddSubCriterion !== undefined;
        const isExpandable = subCriteria.length > 0 || canManageSubs;
        const isOpen = expanded.has(index);
        return (
          <Box
            key={`${item.type}-${item.name ?? ""}-${index}`}
            data-testid="award-criterion-row"
            borderBottomWidth={index === items.length - 1 ? "0" : "1px"}
            borderColor="smBorderSubtle"
            bg={index >= firstMarked ? "smSuccessSoft" : undefined}
          >
            <Flex
              align="center"
              gap="2"
              px="4"
              py="3"
              data-group
              _hover={readOnly ? undefined : { bg: "smBackgroundSecondary" }}
              {...(readOnly ? {} : rowOpenProps(() => onEdit(index)))}
            >
              {isExpandable && (
                <IconButton
                  aria-label={
                    (isOpen ? t("Collapse {{criterion}}", { criterion: name }) : t("Expand {{criterion}}", { criterion: name })) ?? ""
                  }
                  aria-expanded={isOpen}
                  icon={isOpen ? <ChevronDownIcon /> : <ChevronRightIcon />}
                  size="xs"
                  variant="ghost"
                  flexShrink={0}
                  onClick={() => toggle(index)}
                />
              )}
              <Box flex="1" minW="0">
                <Flex align="center" gap="2">
                  <Text fontWeight="bold" fontSize="sm" color="smText">
                    {name}
                  </Text>
                  {subCriteria.length > 0 && (
                    <Badge bg="smBackgroundSecondary" color="smMuted" fontSize="2xs" textTransform="none">
                      {t("{{count}} sub-criterion", { count: subCriteria.length })}
                    </Badge>
                  )}
                </Flex>
                {detail && (
                  <Text fontSize="xs" color="smMuted" noOfLines={2}>
                    {detail}
                  </Text>
                )}
              </Box>
              {/* Outside the readOnly branch: a status describes the content, not what may be done to
                  it, so the SourceMagnet standard carries one too. */}
              <Flex flexShrink={0}>{criterionChip(item)}</Flex>
              {!readOnly && (
                <Flex align="center" flexShrink={0} whiteSpace="nowrap">
                  <IconButton
                    aria-label={terms.deleteCriterion}
                    icon={<DeleteIcon />}
                    size="xs"
                    variant="ghost"
                    onClick={() => onDelete(index)}
                  />
                </Flex>
              )}
            </Flex>

            {/* The same tinted block the wizard's table folds under a criterion. It carries no side
                padding: each row takes it instead, so a row's tint and rule run the full width of the
                card while its text stays indented under the criterion. */}
            {isExpandable && isOpen && (
              <Box
                // No top padding: the first requirement sits straight against the criterion's rule, the
                // way a table row follows the one above it.
                pb="3"
                bg="smStatusNeutralBg"
                borderTopWidth="1px"
                // The same weight Chakra's table draws between rows, so the block matches the one the
                // wizard folds under a criterion rather than reading heavier than it.
                borderColor="smBorderSubtle"
              >
                {subCriteria.length === 0 ? (
                  // The row's own chip already says there are none; the sentence would be the same thing
                  // twice on one row. Opening an empty criterion is for the buttons below it.
                  <Box pt="3" />
                ) : (
                  <VStack align="stretch" spacing="0" mb={canManageSubs ? "3" : "0"}>
                    {subCriteria.map((sub, subIndex) => (
                      <Flex
                        key={sub.id ?? `${sub.name}-${subIndex}`}
                        data-testid="library-sub-criterion-row"
                        align="center"
                        gap="2"
                        py="2"
                        pl="10"
                        pr="4"
                        borderBottomWidth="1px"
                        borderColor="smBorderSubtle"
                        role="group"
                        _hover={canManageSubs ? { bg: "smBackgroundSecondary" } : undefined}
                        {...(canManageSubs
                          ? rowOpenProps(() => onEditSubCriterion?.(index, subIndex))
                          : {})}
                      >
                        <Text fontSize="sm" color="smText" flex="1" minW="0" noOfLines={1}>
                          {sub.name}
                        </Text>
                        {/* The share of this criterion the requirement carries. It is part of the
                            definition — the shares total 100 within the criterion and travel with it —
                            so it is edited where it is read rather than only behind the modal. */}
                        {canManageSubs && onSubCriterionWeightChange ? (
                          // The row opens the modal on click and skips anything that is itself a
                          // control — but Chakra's steppers are aria-hidden divs, not buttons, so
                          // stepping a weight would open the modal underneath it.
                          <Box flexShrink={0} onClick={(e) => e.stopPropagation()}>
                            <WeightInput
                              value={sub.weight?.number}
                              isDisabled={false}
                              isQuiet
                              ariaLabel={
                                t("Weight of {{requirement}}", { requirement: sub.name ?? "" }) ?? ""
                              }
                              onChange={(weight) => onSubCriterionWeightChange(index, subIndex, weight)}
                            />
                          </Box>
                        ) : (
                          <Text fontSize="sm" color="smMuted" flexShrink={0} whiteSpace="nowrap">
                            {sub.weight?.number != null ? `${sub.weight.number} %` : "—"}
                          </Text>
                        )}
                        <Flex flexShrink={0}>{subCriterionChip(item, sub)}</Flex>
                        {canManageSubs && (
                          <Flex align="center" flexShrink={0} whiteSpace="nowrap">
                            <IconButton
                              aria-label={t("Delete sub-criterion")}
                              icon={<DeleteIcon />}
                              size="xs"
                              variant="ghost"
                              onClick={() => onDeleteSubCriterion?.(index, subIndex)}
                            />
                          </Flex>
                        )}
                      </Flex>
                    ))}
                  </VStack>
                )}

                {/* Outlined rather than filled, and sized like the wizard's: the action nested under a
                    criterion must not compete with the one that adds a criterion. */}
                {canManageSubs && (
                  <Flex gap="3" wrap="wrap" pl="10" pr="4">
                    <Button
                      size="sm"
                      variant="outline"
                      bg="smBackground"
                      color="smPrimary"
                      borderColor="smPrimary"
                      leftIcon={<AddIcon boxSize="2.5" />}
                      onClick={() => onAddSubCriterion?.(index)}
                    >
                      {t("New sub-criterion")}
                    </Button>
                    {onAddSubCriterionFromLibrary && (
                      <Button
                        size="sm"
                        variant="outline"
                        bg="smBackground"
                        borderColor="smBorder"
                        leftIcon={<Icon as={FiBookOpen} />}
                        onClick={() => onAddSubCriterionFromLibrary(index)}
                      >
                        {t("Choose from the library")}
                      </Button>
                    )}
                  </Flex>
                )}
              </Box>
            )}
          </Box>
        );
      })}
    </Box>
  );
};
